// CB:BEGIN accept-terms-page
/**
 * Shown when GET /api/me returns 403 REQUIRES_ACCEPTANCE.
 * User must accept current ToS and Privacy versions; then we POST /api/compliance/accept and redirect.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

const boxStyle: React.CSSProperties = {
  maxWidth: 480,
  margin: '60px auto',
  padding: 24,
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  backgroundColor: '#fff',
};

export default function AcceptTermsPage({
  onNavigate,
  onAccepted,
}: {
  onNavigate?: (screen: string) => void;
  onAccepted?: () => void;
}) {
  const { getToken } = useAuth();
  const [versions, setVersions] = useState<{ currentTermsVersion: string; currentPrivacyVersion: string } | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl('/api/compliance/versions'), { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setVersions({ currentTermsVersion: data.currentTermsVersion ?? '1.0', currentPrivacyVersion: data.currentPrivacyVersion ?? '1.0' });
      } catch {
        if (!cancelled) setVersions({ currentTermsVersion: '1.0', currentPrivacyVersion: '1.0' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accepted || !versions) return;
    setBusy(true);
    setError(null);
    try {
      const token = await getToken?.();
      const res = await fetch(getApiUrl('/api/compliance/accept'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          termsVersion: versions.currentTermsVersion,
          privacyVersion: versions.currentPrivacyVersion,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        setError(t || 'Failed to record acceptance');
        return;
      }
      onAccepted?.();
      if (onNavigate) {
        onNavigate('/dashboard');
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err: any) {
      setError(err?.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={boxStyle}>
      <h1 style={{ marginTop: 0, fontSize: '1.25rem' }}>Accept terms to continue</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        We've updated our Terms of Service and Privacy Policy. Please accept them to continue using CoreBids.
      </p>
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={!versions}
          />
          <span>
            I have read and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </span>
        </label>
        {error && <p style={{ color: '#dc2626', marginBottom: 16 }}>{error}</p>}
        <button type="submit" disabled={!accepted || busy} style={{ padding: '10px 20px', cursor: accepted && !busy ? 'pointer' : 'not-allowed' }}>
          {busy ? 'Submitting…' : 'Accept and continue'}
        </button>
      </form>
    </div>
  );
}
// CB:END accept-terms-page
